/* Small analytics card shown in the feed empty state with today's activity summary. */

import { useQuery } from "convex/react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { RoutePath } from "@/routePaths";
import { Inbox, ListChecks, Trash2 } from "lucide-react";
import { getLocalTodayStart } from "@/lib/getLocalTodayStart";

interface FeedEmptyStatsProps {
	queueCount?: number;
}

export function FeedEmptyStats({ queueCount }: FeedEmptyStatsProps) {
	const navigate = useNavigate();
	const todayStart = getLocalTodayStart();
	const lastUpdateItems = useQuery(api.feedUpdateHistory.lastUpdateFilteredCount) ?? 0;
	const queuedToday = useQuery(api.feedItems.countQueuedToday, { todayStart }) ?? 0;
	const dismissedToday = useQuery(api.feedItems.countDismissedToday, { todayStart }) ?? 0;

	const hasAnyData = lastUpdateItems > 0 || queuedToday > 0 || dismissedToday > 0;
	if (!hasAnyData) return null;

	return (
		<div className="mt-6 mx-auto w-fit">
			<div className="rounded-lg border border-border/50 bg-muted/30 px-4 py-3">
				<div className="flex flex-col gap-2">
					<Stat icon={<Inbox className="size-3.5" />} label="Last update" value={`+${lastUpdateItems}`} />
					<Stat icon={<ListChecks className="size-3.5" />} label="Queued today" value={`+${queuedToday}`} valueClassName="text-green-500/70" />
					<Stat icon={<Trash2 className="size-3.5" />} label="Dismissed today" value={`-${dismissedToday}`} valueClassName="text-red-500/70" />
				</div>
			</div>
			{(queueCount ?? 0) > 0 && (
				<Button
					variant="outline"
					size="sm"
					className="mt-3"
					onClick={() => navigate(RoutePath.Queue)}
				>
					Go to Queue ({queueCount})
				</Button>
			)}
		</div>
	);
}

function Stat({ icon, label, value, valueClassName }: { icon: React.ReactNode; label: string; value: string; valueClassName?: string }) {
	return (
		<div className="flex items-center gap-2">
			<div className="flex items-center gap-1 text-muted-foreground/60">
				{icon}
				<span className="text-xs">{label}</span>
			</div>
			<span className={`text-xs font-medium ${valueClassName ?? "text-foreground"}`}>{value}</span>
		</div>
	);
}
